import React, {
  useState,
  useContext,
  useEffect,
  useCallback,
  useRef,
  useLayoutEffect,
  useMemo
} from "react";
import {createPortal} from "react-dom";
import PropTypes from "prop-types";
import styled from "styled-components";
import dayjs from "dayjs";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faSearch, faEllipsisVertical} from "@fortawesome/free-solid-svg-icons";

// Hooks
import useMountedState from "../../hooks/useMountedState.js";
import useApi from "../../hooks/useApi.js";
import usePrevious from "../../hooks/usePrevious.js";

// Contexts
import {useToast} from "../../contexts/toast.js";
import {AuthContext} from "../../contexts/auth.js";
import {FacilityNavContext} from "../../contexts/facilitynav.js";

// Utils
import {exists} from "../../utils/helpers.js";

// Components
import MasonryColumn from "./MasonryColumn.js";
import MasonryContainer from "./MasonryContainer.js";
import Statistic from "../../components/Statistic.js";
import Chart from "../../components/Chart.js";
import Dropdown from "../../components/Dropdown.js";
import ModalPreviewAnalytic from "./ModalPreviewAnalytic.js";
import ModalCompareAnalytic from "./ModalCompareAnalytic.js";

// Style
import {pad, radius, border, transition} from "../../style/components/variables.js";
import {flex, z} from "../../style/components/mixins.js";
import {
  Loader,
  NotLoaded,
  Masonry,
  MasonryItem,
  Text,
  MasonryColumnWrapper,
  Button,
  SearchWrapper,
  Search,
  Input,
  Label,
  SearchIcon
} from "../../style/components/general.js";
import {FIELD} from "../../utils/builder.js";

const dropdownOptions = [
  "All Time",
  "Last 7 Days",
  "Last 30 Days",
  "Last 90 Days",
  "Last Year",
  "Custom Range"
];

const Analytics = ({facility, actionMenuEl}) => {
  const isMounted = useMountedState();

  const {currentUser} = useContext(AuthContext);
  const {analytics, setAnalytics, allOrder, setAllOrder} = useContext(FacilityNavContext);

  const {addToast} = useToast();

  const {loading, api} = useApi("facility-analytics");
  const {api: apiChecksheets} = useApi("checksheets");

  const [checksheets, setChecksheets] = useState();
  // Options
  const [search, setSearch] = useState();
  const [snapshot, setSnapshot] = useState();
  const [start, setStart] = useState();
  const [end, setEnd] = useState();
  // Modals
  const [currentAnalytic, setCurrentAnalytic] = useState({});
  const [showModalPreview, setShowModalPreview] = useState(false);
  const [showModalCompare, setShowModalCompare] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState(false);

  const getInitialColumnCount = () => {
    if (window.innerWidth >= 767) return 3;
    if (window.innerWidth >= 640) return 2;
    return 1;
  };

  const columnCount = useRef(getInitialColumnCount());
  const visible = useRef();
  const menuWrapper = useRef();
  const allLoaded = useRef(false);
  const scrollPos = useRef(null);
  const refreshing = useRef(false);
  const showLoader = useRef(false);
  const timeout = useRef(false);

  const prevSnapshot = usePrevious(snapshot);
  const prevSearch = usePrevious(search);

  const analyticsCount = analytics?.flatMap(row => row).length || 0;

  const noAnalyticsMessage = useMemo(() => {
    if (search?.length > 0) return "No Analytics found.";
    if (
      checksheets &&
      (checksheets.length === 0 ||
        checksheets.every(
          ({builder, state}) =>
            Object.values(builder.byId).every(
              field => field.element !== FIELD || field.type !== "number"
            ) &&
            (!state?.builder?.byId ||
              Object.values(state.builder.byId).every(
                field => field.element !== FIELD || field.type !== "number"
              ))
        ))
    )
      return "No numerical data is currently being recorded";
    return "No Analytics have been created.";
  }, [checksheets, search]);

  // Initial Load
  useEffect(() => {
    if (isMounted() && facility) {
      // Start from inital values when selected

      apiChecksheets
        .callGet(null, {facilityId: facility.id, withAllStates: true, withBuilder: true})
        .then(({status, data}) => {
          if (status === 200 && data) {
            setChecksheets(data);
          }
        });
    }
  }, [isMounted, apiChecksheets, facility]);

  useEffect(() => {
    const handleClickOutside = e => {
      if (!showActionsMenu) return;
      if (menuWrapper.current && !menuWrapper.current.contains(e.target)) {
        setShowActionsMenu(false);
      }
    };

    const handleEscape = e => {
      if (e.key === "Escape") setShowActionsMenu(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [showActionsMenu]);

  const rearrange = useCallback(
    data => {
      let columns = [];
      if (visible.current && !refreshing.current)
        columns = visible.current.map(col => new MasonryColumn(col));
      else
        for (let i = 0; i < columnCount.current; i++) {
          columns.push(new MasonryColumn());
        }

      const CHART_RATIO = 0.38;
      const STAT_HEIGHT = 103;
      const GAP_HEIGHT = 10;

      const getChartHeight = item => {
        let height = 0;

        if (item.type === "plot") height += window.innerHeight * CHART_RATIO;
        else height += STAT_HEIGHT;
        height += GAP_HEIGHT;
        return height;
      };

      const container = new MasonryContainer(columns, getChartHeight);

      data.map(analytic => {
        if (!(currentUser.type.name === "general" && !analytic.active)) {
          container.insert(analytic);
        }
      });

      const columnArray = container.extract();

      if (columnArray.every(col => !col?.length)) return [];

      return columnArray;
    },
    [currentUser.type.name]
  );

  useLayoutEffect(() => {
    if (analytics && refreshing.current && exists(scrollPos.current)) {
      document.documentElement.scrollTop = scrollPos.current;
      refreshing.current = false;
      scrollPos.current = null;
    }
  }, [analytics]);

  const loadAnalytics = useCallback(
    (currIdx, searchString) => {
      // reset
      if (!currIdx) {
        visible.current = null;
        allLoaded.current = false;
      }

      const rect = menuWrapper.current?.getBoundingClientRect();

      const params = {
        facilityId: facility.id,
        evaluate: true,
        snapshot: snapshot,
        start,
        end: end && dayjs(end).add(1, "day").format("YYYY-MM-DD"),
        idx: currIdx || 0,
        numColumns: columnCount.current,
        // this height is the height from the top of analytics (bottom of menu) to bottom of viewport
        height: Math.ceil(
          rect ? Math.abs(window.innerHeight - (rect.top + rect.height)) : window.innerHeight
        ),
        viewportHeight: Math.ceil(window.innerHeight),
        // if this is true, we end at idx, otherwise we begin at idx (in terms of analytics retrieved - idx is equivalent to order)
        refreshing: !!currIdx && refreshing.current
      };

      if (searchString) params.filter = JSON.stringify({Search: searchString});

      api.callGet(null, params).then(({status, data}) => {
        if (status === 200 && data) {
          const ordered = rearrange(data.analytics);
          visible.current = ordered;
          setAnalytics(ordered);
          setAllOrder(data.allOrder);
          if (data.containsFinalAnalytic) allLoaded.current = true;
        } else if (status) {
          setAnalytics([]);
          setAllOrder([]);
        }
      });
    },
    [facility, snapshot, start, end, api, rearrange, setAnalytics, setAllOrder]
  );

  const handleInputRateLimit = useCallback(
    (currIdx, searchString) => {
      if (searchString !== undefined) setSearch(searchString);
      if (timeout.current) clearTimeout(timeout.current);
      const timer = setTimeout(() => {
        loadAnalytics(currIdx, searchString);
      }, 100);
      timeout.current = timer;
    },
    [loadAnalytics]
  );

  // Initial Load
  useEffect(() => {
    if (isMounted() && facility?.id && analytics == null) handleInputRateLimit();

    return () => {
      if (!isMounted()) {
        setAnalytics(null);
        setAllOrder(null);
      }
    };
  }, [isMounted, facility, analytics, handleInputRateLimit, setAnalytics, setAllOrder]);

  useEffect(() => {
    if (snapshot && prevSnapshot !== snapshot) {
      scrollPos.current = document.documentElement.scrollTop;
      refreshing.current = true;
      showLoader.current = true;
      handleInputRateLimit(analyticsCount, search);
    }
  }, [snapshot, prevSnapshot, search, analyticsCount, handleInputRateLimit]);

  useEffect(() => {
    if (search && prevSearch !== search) {
      showLoader.current = true;
      handleInputRateLimit(null, search);
    }
  }, [search, prevSearch, analytics, analyticsCount, handleInputRateLimit]);

  useEffect(() => {
    const handleScroll = () => {
      if (
        window.innerHeight + Math.ceil(window.scrollY) >= document.documentElement.offsetHeight &&
        !allLoaded.current &&
        !loading
      ) {
        showLoader.current = true;
        handleInputRateLimit(analyticsCount, search);
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, [analytics, loading, handleInputRateLimit, search, analyticsCount]);

  // Handle Range Error
  useEffect(() => {
    if (isMounted() && start && end && start > end)
      addToast("Start date must occur before end date. Please adjust range.", "warning");
  }, [isMounted, addToast, start, end]);

  // Handle Search & Range Change
  useEffect(() => {
    if (isMounted() && snapshot && snapshot !== "custom range") {
      // Snapshot takes priority over custom range
      setStart(null);
      setEnd(null);
    }
  }, [isMounted, snapshot]);

  // Handle Screen Size Change
  useEffect(() => {
    const resize = () => {
      const prev = columnCount.current;
      if (window.innerWidth >= 767) columnCount.current = 3;
      else if (window.innerWidth >= 640) columnCount.current = 2;
      else columnCount.current = 1;
      // changed
      if (prev !== columnCount.current) {
        showLoader.current = true;
        visible.current = null;
        setAnalytics(null);

        handleInputRateLimit(null, search);
      }
    };

    window.addEventListener("resize", resize);

    return () => window.removeEventListener("resize", resize);
  }, [handleInputRateLimit, rearrange, setAnalytics, analytics, search]);

  const reload = () => {
    scrollPos.current = document.documentElement.scrollTop;
    refreshing.current = true;
    showLoader.current = true;
    handleInputRateLimit();
  };

  const hasFilters = !!search || !!snapshot;

  const actionsMenu = (
    <ActionsMenuWrapper ref={menuWrapper}>
      <ActionMenuButton
        title="More"
        type="button"
        $active={hasFilters || showActionsMenu}
        aria-label="Analytics actions"
        aria-expanded={showActionsMenu}
        aria-haspopup="true"
        data-testid="analytics.moreActions"
        onClick={() => setShowActionsMenu(prev => !prev)}>
        <FontAwesomeIcon icon={faEllipsisVertical} />
      </ActionMenuButton>
      {showActionsMenu && (
        <ActionsMenu>
          <MenuSearch>
            <SearchWrapper>
              <SearchIcon>
                <FontAwesomeIcon icon={faSearch} />
              </SearchIcon>
              <StyledSearch
                type="text"
                placeholder="Analytics..."
                autoComplete="off"
                autoFocus
                value={search || ""}
                onChange={e => {
                  showLoader.current = true;
                  handleInputRateLimit(null, e.target.value);
                }}
              />
            </SearchWrapper>
          </MenuSearch>
          {analyticsCount > 0 && (
            <MenuDropdown>
              <Dropdown
                options={dropdownOptions}
                placeholder="Apply Date Range..."
                selection={snapshot}
                setSelection={setSnapshot}
                showPlaceholder
                fullWidth
              />
            </MenuDropdown>
          )}
          {analyticsCount > 0 && snapshot === "custom range" && (
            <MenuRange>
              <RangeField>
                <Label htmlFor="start" bold>
                  From
                </Label>
                <Input
                  type="date"
                  name="start"
                  value={start || ""}
                  onChange={e => setStart(e.target.value)}
                  placeholder="Select Date ..."
                  hasValue={start}
                />
              </RangeField>
              <RangeField>
                <Label htmlFor="end" bold>
                  To
                </Label>
                <Input
                  type="date"
                  name="end"
                  value={end || ""}
                  onChange={e => setEnd(e.target.value)}
                  placeholder="Select Date ..."
                  hasValue={end}
                />
              </RangeField>
            </MenuRange>
          )}
          {((analytics && analyticsCount > 1) || facility.types?.length > 0) && (
            <ActionItem
              type="button"
              onClick={() => {
                setShowModalCompare(true);
                setShowActionsMenu(false);
              }}>
              Compare Charts
            </ActionItem>
          )}
        </ActionsMenu>
      )}
    </ActionsMenuWrapper>
  );

  return (
    <>
      {actionMenuEl
        ? createPortal(actionsMenu, actionMenuEl)
        : (analyticsCount > 0 || !!search) && <InlineMenu>{actionsMenu}</InlineMenu>}

      {analytics && (
        <Masonry>
          {analyticsCount > 0 ? (
            analytics?.map((col, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <MasonryColumnWrapper key={`analytic-col-${i}`}>
                {col.map(analytic => (
                  <MasonryItem data-testid="analytics" key={`analytic-${analytic.id}`}>
                    <button
                      type="button"
                      onClick={() => {
                        setCurrentAnalytic(analytic);
                        setShowModalPreview(true);
                      }}>
                      {["average", "min", "max", "total", "median"].includes(analytic.type) && (
                        <Statistic stat={analytic} />
                      )}
                      {analytic.type === "completionrate" && (
                        <Statistic stat={analytic} precision={0} />
                      )}
                      {analytic.type === "plot" && (
                        <Chart
                          chart={analytic}
                          allSources={analytic.sources}
                          label={analytic.label}
                          view="analytics"
                          chartType={analytic.chartType}
                        />
                      )}
                    </button>
                  </MasonryItem>
                ))}
              </MasonryColumnWrapper>
            ))
          ) : (
            <NotFound>
              <Text>{noAnalyticsMessage}</Text>
            </NotFound>
          )}
        </Masonry>
      )}

      {loading && (
        <NotLoaded>
          <Loader />
        </NotLoaded>
      )}

      {showModalPreview && (
        <ModalPreviewAnalytic
          visible={showModalPreview}
          setVisible={setShowModalPreview}
          analytic={currentAnalytic}
          reload={reload}
          allOrder={allOrder}
        />
      )}

      {showModalCompare && (
        <ModalCompareAnalytic visible={showModalCompare} setVisible={setShowModalCompare} />
      )}
    </>
  );
};

Analytics.propTypes = {
  facility: PropTypes.objectOf(PropTypes.any).isRequired,
  actionMenuEl: PropTypes.object
};

// Style Overrides
const InlineMenu = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: ${pad}px;
`;

const ActionsMenuWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: stretch;
`;

const ActionMenuButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-width: 36px;
  min-height: 32px;
  padding-left: ${pad}px;
  color: ${({theme}) => theme.secondary};
  background-color: transparent;
  transition: ${transition};

  svg {
    fill: ${({theme}) => theme.secondary};
  }

  &:hover {
    background-color: ${({theme}) => theme.secondary};
    color: ${({theme}) => theme.tertiary};

    svg {
      fill: ${({theme}) => theme.tertiary};
    }
  }

  ${({$active, theme}) =>
    $active &&
    `
      background-color: ${theme.secondary};
      color: ${theme.tertiary};

      svg {
        fill: ${theme.tertiary};
      }
    `}
`;

const ActionsMenu = styled.div`
  position: absolute;
  top: calc(100% + ${pad / 2}px);
  right: 0;
  min-width: 220px;
  background: ${({theme}) => theme.tertiary};
  border: ${border} solid ${({theme}) => theme.secondary};
  border-radius: ${radius};
  box-shadow: 0 3px 5px rgb(0, 0, 0, 0.5);
  z-index: ${z("higher")};
  overflow: hidden;
`;

const MenuSearch = styled.div`
  padding: ${pad / 2}px;

  ${SearchWrapper} {
    width: 100%;
  }

  ${SearchIcon} {
    top: 50%;
    transform: translateY(-50%);
  }
`;

const MenuDropdown = styled.div`
  padding: 0 ${pad / 2}px ${pad / 2}px;

  select {
    max-width: 100%;
    width: 100%;
  }
`;

const MenuRange = styled.div`
  ${flex("column")};
  padding: 0 ${pad / 2}px ${pad / 2}px;
  gap: ${pad / 2}px;

  input[type="date"] {
    width: 100%;
  }
`;

const RangeField = styled.div`
  ${flex("column")};
  gap: ${pad / 4}px;
`;

const ActionItem = styled.button`
  ${flex("row", "nowrap", "start", "center")};
  gap: ${pad / 2}px;
  width: 100%;
  padding: ${pad / 2}px ${pad}px;
  background: ${({theme}) => theme.tertiary};
  color: ${({theme}) => theme.secondary};
  text-align: left;

  &:hover:not(:disabled) {
    color: ${({theme}) => theme.tertiary};
    background: ${({theme}) => theme.secondary};
  }
`;

const StyledSearch = styled(Search)`
  padding-left: 30px;

  &[type="text"] {
    max-width: 100%;
    width: 100%;
  }
`;

const NotFound = styled(MasonryItem)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 75px;
  padding: ${pad}px;
  border-radius: ${radius};
  border: ${border} solid ${({theme}) => theme.secondary};
  max-width: 100% !important;
  flex: unset;
`;

export default Analytics;
